/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx } from '@emotion/core';
import { message, Modal } from 'antd';
import {
  useMemo,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
} from 'react';
import { useIntl } from 'react-intl';
import { IconName, IconPrefix } from '@fortawesome/fontawesome-svg-core';
import { useProjectBuilder } from '~src/hooks/api-hooks/useProjectBuilder';
import { syncArchiveFolderStructure } from '~src/services/api/projectBuilder';
import { useProjectId } from '~src/hooks/useProjectId';
import { usePreventNavigation } from '~src/hooks/usePreventNavigation';
import { ThemedStyleFn, useStyles } from '~src/hooks/useStyles';
import { SearchContext } from '~src/contexts/SearchContext';
import { filterByQuery } from '~src/utils/searchFilters';
import { CreateFolderModal } from '~src/components/popups-modals/CreateFolderModal/CreateFolderModal';
import { ReorganizeFoldersModal } from '~src/components/popups-modals/ReorganizeFoldersModal/ReorganizeFoldersModal';
import { EditFolderProjectBuilder } from '~src/components/popups-modals/EditFolderProjectBuilder/EditFolderProjectBuilder';
import { MoveFolderProjectBuilder } from '~src/components/popups-modals/MoveFolderProjectBuilder/MoveFolderProjectBuilder';
import { ImportToProjectBuilder } from '~src/components/popups-modals/ImportToProjectBuilder/ImportToProjectBuilder';
import { ImportTemplateModal } from '~src/components/popups-modals/ImportTemplateModal/ImportTemplateModal';
import { expandFolderPaths } from '~src/utils/zipImportUtils';
import { ProjectBuilderTable } from './components/ProjectBuilderTable';
import {
  dropTargetForElements,
  monitorForElements,
} from '@atlaskit/pragmatic-drag-and-drop/element/adapter';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useDynamicHeightAndLockScroll } from '~src/hooks/useDynamicHeightAndLockScroll';
import { BaseRightClickOverlay } from '~src/components/atoms/RightClickOverlay/BaseRightClickOverlay';
import dataContextMenu, {
  DataContextMenuItemType,
} from '~src/data/dataContextMenu';

const RightSideViewProjectBuilder = () => {
  const intl = useIntl();
  const styles = useStyles(styleFn);
  const projectBuilderQuery = useProjectBuilder();
  const projectId = useProjectId();
  const searchContext = useContext(SearchContext);

  // State for new folders (cache-based)
  const [newFolders, setNewFolders] = useState<any[]>([]);
  const [nextId, setNextId] = useState(-1);
  const [createModalVisible, setCreateModalVisible] = useState(false);
  const [createModalParentId, setCreateModalParentId] = useState<number | null>(
    null
  );
  const [reorganizeModalVisible, setReorganizeModalVisible] = useState(false);

  // Context menu state
  const [rightClickFolder, setRightClickFolder] = useState<any | null>(null);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [moveModalVisible, setMoveModalVisible] = useState(false);
  const [importToModalVisible, setImportToModalVisible] = useState(false);
  const [importTemplateModalVisible, setImportTemplateModalVisible] = useState(
    false
  );
  const [importTemplateTargetFolder, setImportTemplateTargetFolder] = useState<{
    id: number;
    name: string;
  } | null>(null);

  // Expand/collapse state
  const [expandedFolders, setExpandedFolders] = useState<Set<number>>(
    new Set()
  );

  // Root drop zone state
  const [isRootDropTarget, setIsRootDropTarget] = useState(false);
  const [isDraggingAnyFolder, setIsDraggingAnyFolder] = useState(false);
  const rootDropZoneRef = useRef<HTMLDivElement>(null);

  // Dynamic height hook for proper scrolling
  const {
    containerRef,
    dynamicHeight,
  } = useDynamicHeightAndLockScroll<HTMLDivElement>();

  // Event listener for "Create New Folder" button in header
  useEffect(() => {
    const handleOpenModal = () => {
      setCreateModalVisible(true);
    };

    const handleClearUnsavedFolders = () => {
      setNewFolders([]);
    };

    window.addEventListener('openCreateFolderModal', handleOpenModal);
    window.addEventListener(
      'clearProjectBuilderUnsavedFolders',
      handleClearUnsavedFolders
    );
    return () => {
      window.removeEventListener('openCreateFolderModal', handleOpenModal);
      window.removeEventListener(
        'clearProjectBuilderUnsavedFolders',
        handleClearUnsavedFolders
      );
    };
  }, []);

  // Event listener for "Reorganize" button in header
  useEffect(() => {
    const handleOpenModal = () => {
      setReorganizeModalVisible(true);
    };

    window.addEventListener('openReorganizeFoldersModal', handleOpenModal);
    return () => {
      window.removeEventListener('openReorganizeFoldersModal', handleOpenModal);
    };
  }, []);

  // Broadcast new folders count to header for button state
  useEffect(() => {
    window.dispatchEvent(
      new CustomEvent('newFoldersCountChanged', {
        detail: { count: newFolders.length },
      })
    );
  }, [newFolders.length]);

  // Monitor drag operations globally to show/hide root drop zone
  useEffect(() => {
    return monitorForElements({
      onDragStart: () => setIsDraggingAnyFolder(true),
      onDrop: () => setIsDraggingAnyFolder(false),
    });
  }, []);

  // Warn user before leaving page with unsaved folders
  useEffect(() => {
    if (newFolders.length === 0) return;

    const beforeUnloadListener = (event: BeforeUnloadEvent) => {
      event.preventDefault();

      event.returnValue = intl.formatMessage({
        id: 'prevent-navigation-unsaved-folders',
      });

      return event.returnValue;
    };

    window.addEventListener('beforeunload', beforeUnloadListener, {
      capture: true,
    });

    return () => {
      window.removeEventListener('beforeunload', beforeUnloadListener, {
        capture: true,
      });
    };
  }, [newFolders.length, intl]);

  // Track unsaved folders in sessionStorage for Settings component
  useEffect(() => {
    if (newFolders.length > 0) {
      window.sessionStorage.setItem('pb-unsaved-folders', 'true');
    } else {
      window.sessionStorage.removeItem('pb-unsaved-folders');
    }
  }, [newFolders.length]);

  // Prevent route navigation with unsaved folders
  usePreventNavigation({
    prevent: newFolders.length > 0,
    modalFuncProps: {
      title: intl.formatMessage({ id: 'you-sure' }),
      content: intl.formatMessage({
        id: 'prevent-navigation-unsaved-folders',
      }),
    },
  });

  // Handler for creating new folder
  const handleCreateFolder = (folderData: {
    name: string;
    description: string;
  }) => {
    // Auto-rename if duplicate exists
    const uniqueName = getUniqueFolderName(
      folderData.name,
      createModalParentId
    );

    const newFolder = {
      id: nextId,
      name: uniqueName, // Use unique name instead of original
      description: folderData.description,
      parent_id: createModalParentId,
      isNew: true,
      is_locked: false,
      is_favorite: false,
    };
    setNewFolders([...newFolders, newFolder]);
    setNextId(nextId - 1);

    // Auto-expand parent if creating as child
    if (createModalParentId !== null) {
      setExpandedFolders(
        new Set([...Array.from(expandedFolders), createModalParentId])
      );
    }

    // Reset parent ID for next creation
    setCreateModalParentId(null);

    // Show different message if name was changed
    if (uniqueName !== folderData.name) {
      message.info(
        intl.formatMessage(
          { id: 'folder-created-with-renamed' },
          { original: folderData.name, renamed: uniqueName }
        )
      );
    } else {
      message.success(intl.formatMessage({ id: 'folder-created-locally' }));
    }
  };

  // Handler for saving reorganized folders from modal
  const handleSaveReorganized = (reorganizedFolders: any[]) => {
    setNewFolders(reorganizedFolders);
    message.success(intl.formatMessage({ id: 'folders-reorganized' }));
  };

  // Handler for importing folder structure from a ZIP file
  const handleImportZipFolders = useCallback(
    (e: CustomEvent) => {
      const { selectedPaths, targetFolderId } = e.detail;
      if (!selectedPaths || selectedPaths.length === 0) return;

      // Sort selected paths by depth (shortest first)
      const sortedPaths = [...selectedPaths].sort((a, b) => {
        const depthA = a.split('/').length;
        const depthB = b.split('/').length;
        return depthA - depthB;
      });

      // Build a map of original path → new folder ID
      const pathToId = new Map<string, number>();
      let currentNextId = nextId;
      const newEntries: any[] = [];

      sortedPaths.forEach((path) => {
        const segments = path.split('/').filter(Boolean);
        const name = segments[segments.length - 1];

        // Check if this path's parent is in the selected paths
        const parentPath =
          segments.length > 1 ? segments.slice(0, -1).join('/') : '';
        const hasSelectedParent =
          parentPath && selectedPaths.includes(parentPath);

        let parentId: number | null = null;
        if (hasSelectedParent) {
          // Parent was also selected, so use its new ID
          parentId = pathToId.get(parentPath) ?? null;
        } else if (targetFolderId) {
          // No selected parent, import as child of target folder
          parentId = targetFolderId;
        }
        // else: import as root folder (parent_id = null)

        // Name conflicts pass through unchanged (same as template import) so
        // they're caught by findDuplicateFolderIds and can be resolved via Merge
        const entry = {
          id: currentNextId,
          name,
          description: '',
          parent_id: parentId,
          isNew: true,
          is_locked: false,
          is_favorite: false,
        };

        pathToId.set(path, currentNextId);
        currentNextId -= 1;
        newEntries.push(entry);
      });

      setNewFolders((prev) => [...prev, ...newEntries]);
      setNextId(currentNextId);

      message.success(
        intl.formatMessage(
          { id: 'import-zip-success' },
          { count: newEntries.length }
        )
      );
    },
    [nextId, newFolders, projectBuilderQuery.data, intl]
  );

  // Event listener for ZIP import from ImportTemplateModal
  useEffect(() => {
    const handler = (e: Event) => handleImportZipFolders(e as CustomEvent);
    window.addEventListener('importZipFolders', handler);
    return () => {
      window.removeEventListener('importZipFolders', handler);
    };
  }, [handleImportZipFolders]);

  // Helper function to detect circular dependencies
  const wouldCreateCircle = useCallback(
    (draggedId: number, targetId: number, folderList: any[]): boolean => {
      // Check if targetId is a descendant of draggedId
      const isDescendant = (parentId: number, childId: number): boolean => {
        const folder = folderList.find((f) => f.id === childId);
        if (!folder || !folder.parent_id) return false;
        if (folder.parent_id === parentId) return true;
        return isDescendant(parentId, folder.parent_id);
      };
      return isDescendant(draggedId, targetId);
    },
    []
  );

  // Helper function to generate unique folder name with incremental numbering
  const getUniqueFolderName = useCallback(
    (
      baseName: string,
      parentId: number | null,
      excludeFolderId?: number
    ): string => {
      const allFolders = [...newFolders, ...(projectBuilderQuery.data || [])];
      const siblings = allFolders.filter(
        (f) => f.parent_id === parentId && f.id !== excludeFolderId
      );

      let name = baseName;
      let counter = 1;

      // Keep incrementing until we find a unique name
      while (
        siblings.some((f) => f.name.toLowerCase() === name.toLowerCase())
      ) {
        name = `${baseName} (${counter})`;
        counter++;
      }

      return name;
    },
    [newFolders, projectBuilderQuery.data]
  );

  // Helper function to find duplicate folders in same directory
  const findDuplicateFolderIds = useCallback(
    (folders: any[]): Set<number> => {
      const duplicateIds = new Set<number>();

      // Combine new and existing folders for comparison
      const allFolders = [...folders, ...(projectBuilderQuery.data || [])];

      // Group folders by parent_id
      const foldersByParent = new Map<number | null, any[]>();
      allFolders.forEach((folder) => {
        const parentId = folder.parent_id;
        if (!foldersByParent.has(parentId)) {
          foldersByParent.set(parentId, []);
        }
        foldersByParent.get(parentId)!.push(folder);
      });

      // Check for duplicates within each parent group
      foldersByParent.forEach((siblings) => {
        const nameCount = new Map<string, any[]>();

        siblings.forEach((folder) => {
          const normalizedName = folder.name.trim().toLowerCase();
          if (!nameCount.has(normalizedName)) {
            nameCount.set(normalizedName, []);
          }
          nameCount.get(normalizedName)!.push(folder);
        });

        // Mark ONLY NEW folders with duplicate names
        nameCount.forEach((foldersWithSameName) => {
          if (foldersWithSameName.length > 1) {
            foldersWithSameName.forEach((f) => {
              // Only mark new folders (negative IDs) as duplicates
              if (f.id < 0) {
                duplicateIds.add(f.id);
              }
            });
          }
        });
      });

      return duplicateIds;
    },
    [projectBuilderQuery.data]
  );

  // Compute duplicate IDs whenever newFolders changes
  const duplicateFolderIds = useMemo(() => {
    return findDuplicateFolderIds(newFolders);
  }, [newFolders, findDuplicateFolderIds]);

  // Dispatch duplicate count to parent components
  useEffect(() => {
    window.dispatchEvent(
      new CustomEvent('projectBuilderDuplicateCount', {
        detail: { count: duplicateFolderIds.size },
      })
    );
  }, [duplicateFolderIds]);

  // Merge duplicate folders function
  const mergeDuplicateFolders = useCallback(() => {
    let currentFolders = [...newFolders];
    const foldersToKeep = new Map<number, any>(); // Maps removed ID -> kept folder
    const idsToRemove = new Set<number>();

    let hasChanges = true;
    let iterationCount = 0;
    const maxIterations = 50;

    // Keep merging until no more duplicates found
    while (hasChanges && iterationCount < maxIterations) {
      iterationCount++;
      hasChanges = false;

      const allFolders = [
        ...currentFolders,
        ...(projectBuilderQuery.data || []),
      ];

      // Track original parent_id before any remapping
      const foldersByOriginalParent = new Map<number | null, any[]>();

      allFolders.forEach((folder) => {
        // Use the folder's current parent_id (don't follow merge mapping yet)
        const parentId = folder.parent_id;
        if (!foldersByOriginalParent.has(parentId)) {
          foldersByOriginalParent.set(parentId, []);
        }
        foldersByOriginalParent.get(parentId)!.push(folder);
      });

      // Find duplicates within each parent group
      foldersByOriginalParent.forEach((siblings) => {
        const nameGroups = new Map<string, any[]>();

        siblings.forEach((folder) => {
          const normalizedName = folder.name.trim().toLowerCase();
          if (!nameGroups.has(normalizedName)) {
            nameGroups.set(normalizedName, []);
          }
          nameGroups.get(normalizedName)!.push(folder);
        });

        nameGroups.forEach((duplicates) => {
          if (duplicates.length > 1) {
            duplicates.sort((a, b) => {
              if (a.id > 0 && b.id < 0) return -1; // EXISTING before NEW
              if (a.id < 0 && b.id > 0) return 1; // NEW after EXISTING
              return a.id - b.id; // Among same type, oldest first (smallest ID)
            });

            const keepFolder = duplicates[0];

            // Merge NEW duplicate folders
            for (let i = 1; i < duplicates.length; i++) {
              const duplicateFolder = duplicates[i];

              // Only process NEW folders (negative IDs)
              if (
                duplicateFolder.id < 0 &&
                !idsToRemove.has(duplicateFolder.id)
              ) {
                foldersToKeep.set(duplicateFolder.id, keepFolder);
                idsToRemove.add(duplicateFolder.id);
                hasChanges = true;
              }
            }
          }
        });
      });

      // If we found duplicates this iteration, update the working set
      if (hasChanges) {
        currentFolders = currentFolders
          .filter((f) => !idsToRemove.has(f.id))
          .map((f) => {
            // Update parent_id if parent was merged
            let newParentId = f.parent_id;
            let checkId = f.parent_id;

            // Follow the merge chain to find final parent
            while (foldersToKeep.has(checkId)) {
              newParentId = foldersToKeep.get(checkId)!.id;
              checkId = newParentId;
            }

            return newParentId !== f.parent_id
              ? { ...f, parent_id: newParentId }
              : f;
          });
      }
    }

    setNewFolders(currentFolders);

    if (idsToRemove.size > 0) {
      message.success(
        intl.formatMessage(
          { id: 'folders-merged-successfully' },
          { count: idsToRemove.size }
        )
      );
    } else {
      message.info(intl.formatMessage({ id: 'no-duplicates-to-merge' }));
    }
  }, [newFolders, projectBuilderQuery.data, intl]);

  // Event listener for "Merge" button in header
  useEffect(() => {
    const handleMergeFolders = () => {
      mergeDuplicateFolders();
    };

    window.addEventListener('mergeDuplicateFolders', handleMergeFolders);
    return () => {
      window.removeEventListener('mergeDuplicateFolders', handleMergeFolders);
    };
  }, [mergeDuplicateFolders]);

  // Helper function to get sibling folder names for validation
  const getSiblingFolderNames = useCallback(
    (parentId: number | null, excludeFolderId?: number): string[] => {
      const allFolders = [...newFolders, ...(projectBuilderQuery.data || [])];
      return allFolders
        .filter((f) => f.parent_id === parentId && f.id !== excludeFolderId)
        .map((f) => f.name);
    },
    [newFolders, projectBuilderQuery.data]
  );

  // Validation for drag operations
  const canNest = useCallback(
    (draggedId: number, targetId: number) => {
      // Can't nest into itself
      if (draggedId === targetId) return false;

      // Can't create circular dependency
      if (wouldCreateCircle(draggedId, targetId, newFolders)) return false;

      // Can only drag new folders
      const draggedFolder = newFolders.find((f) => f.id === draggedId);
      if (!draggedFolder) return false;

      return true;
    },
    [newFolders, wouldCreateCircle]
  );

  // Context menu items with disabled states
  const contextMenuItems = useMemo(() => {
    const baseItems = dataContextMenu.projectBuilderFolder;

    return baseItems.map((item) => {
      const isExistingFolder = rightClickFolder && !rightClickFolder.isNew;
      const shouldDisable =
        (item.name === 'edit' && isExistingFolder) ||
        (item.name === 'remove' && isExistingFolder) ||
        (item.name === 'move-to' && isExistingFolder);

      // Add displayName for specific menu items
      const displayName =
        item.name === 'create-child-folder'
          ? intl.formatMessage({ id: 'create-new-folder-here' })
          : undefined;

      return {
        ...item,
        disabled: shouldDisable,
        disabledTooltip: shouldDisable
          ? 'feature-disabled-existing-folders'
          : undefined,
        displayName,
      };
    });
  }, [rightClickFolder, intl]);

  // Context menu handler
  const handleContextMenuItemClick = (item: DataContextMenuItemType) => {
    if (!rightClickFolder) return;

    switch (item.modal) {
      case 'create-child-folder':
        setCreateModalParentId(rightClickFolder.id);
        setCreateModalVisible(true);
        break;
      case 'edit-folder':
        setEditModalVisible(true);
        break;
      case 'remove-folder':
        handleRemoveFolder(rightClickFolder.id);
        break;
      case 'move-folder':
        setMoveModalVisible(true);
        break;
      case 'import-to-folder':
        setImportToModalVisible(true);
        break;
      case 'import-template-to-folder':
        setImportTemplateTargetFolder({
          id: rightClickFolder.id,
          name: rightClickFolder.name,
        });
        setImportTemplateModalVisible(true);
        break;
    }
  };

  // Handler for removing folder (with confirmation)
  const handleRemoveFolder = (folderId: number) => {
    const folder = newFolders.find((f) => f.id === folderId);
    if (!folder) return;

    // Find all children recursively
    const removeWithChildren = (id: number): number[] => {
      const folder = newFolders.find((f) => f.id === id);
      if (!folder) return [];

      const children = newFolders.filter((f) => f.parent_id === id);
      const childIds = children.flatMap((c) => removeWithChildren(c.id));

      return [id, ...childIds];
    };

    const idsToRemove = removeWithChildren(folderId);
    const childrenCount = idsToRemove.length - 1;

    Modal.confirm({
      title: `${intl.formatMessage({ id: 'confirm-remove-folder' })} "${
        folder.name
      }"?`,
      content:
        childrenCount > 0
          ? intl.formatMessage(
              { id: 'folder-has-children' },
              { count: childrenCount }
            )
          : undefined,
      okText: intl.formatMessage({ id: 'remove' }),
      okType: 'danger',
      cancelText: intl.formatMessage({ id: 'cancel' }),
      onOk: () => {
        const idsSet = new Set(idsToRemove);
        setNewFolders(newFolders.filter((f) => !idsSet.has(f.id)));
        message.success(intl.formatMessage({ id: 'folder-removed' }));
        setRightClickFolder(null);
      },
    });
  };

  // Handler for editing folder
  const handleEditFolder = (data: { name: string; description: string }) => {
    if (!rightClickFolder) return;

    // Auto-rename if duplicate exists
    const uniqueName = getUniqueFolderName(
      data.name,
      rightClickFolder.parent_id,
      rightClickFolder.id
    );

    setNewFolders(
      newFolders.map((f) =>
        f.id === rightClickFolder.id
          ? { ...f, name: uniqueName, description: data.description }
          : f
      )
    );

    // Show different message if name was changed
    if (uniqueName !== data.name) {
      message.info(
        intl.formatMessage(
          { id: 'folder-renamed-due-to-duplicate' },
          { original: data.name, renamed: uniqueName }
        )
      );
    } else {
      message.success(intl.formatMessage({ id: 'folder-updated' }));
    }

    setEditModalVisible(false);
    setRightClickFolder(null);
  };

  // Handler for moving folder
  const handleMoveFolder = (folderId: number, newParentId: number | null) => {
    // Validate no circular dependency
    if (
      newParentId !== null &&
      wouldCreateCircle(folderId, newParentId, newFolders)
    ) {
      message.warning(
        intl.formatMessage({ id: 'circular-dependency-warning' })
      );
      return;
    }

    setNewFolders(
      newFolders.map((f) =>
        f.id === folderId ? { ...f, parent_id: newParentId } : f
      )
    );
    message.success(intl.formatMessage({ id: 'move-folder-success' }));
    setMoveModalVisible(false);
    setRightClickFolder(null);
  };

  // Handler for importing folder into selected folder
  const handleImportToFolder = (
    movingFolderId: number,
    targetFolderId: number
  ) => {
    setNewFolders(
      newFolders.map((f) =>
        f.id === movingFolderId ? { ...f, parent_id: targetFolderId } : f
      )
    );
    setImportToModalVisible(false);
    setRightClickFolder(null);

    // Expand target folder to show the imported folder
    setExpandedFolders(
      new Set([...Array.from(expandedFolders), targetFolderId])
    );

    message.success(intl.formatMessage({ id: 'folder-imported-to-target' }));
  };

  // Handler for nesting folders via DnD
  const handleNestFolder = useCallback(
    (draggedId: number, targetId: number | null) => {
      // Circular dependency check
      if (
        targetId !== null &&
        wouldCreateCircle(draggedId, targetId, newFolders)
      ) {
        message.warning(
          intl.formatMessage({ id: 'circular-dependency-warning' })
        );
        return;
      }

      // Self-nesting check
      if (draggedId === targetId) return;

      // Update parent_id
      const updated = newFolders.map((f) =>
        f.id === draggedId ? { ...f, parent_id: targetId } : f
      );

      setNewFolders(updated);

      // Auto-expand target if it exists
      if (targetId !== null) {
        setExpandedFolders((prev) => new Set([...Array.from(prev), targetId]));
      }
    },
    [newFolders, wouldCreateCircle, intl]
  );

  // Expand/collapse handler
  const handleToggleExpand = useCallback((folderId: number) => {
    setExpandedFolders((prev) => {
      const next = new Set(prev);
      if (next.has(folderId)) {
        next.delete(folderId);
      } else {
        next.add(folderId);
      }
      return next;
    });
  }, []);

  // Handler for making folders root level (set parent_id to null)
  const handleMakeRoot = useCallback(
    (folderId: number) => {
      const updated = newFolders.map((f) =>
        f.id === folderId ? { ...f, parent_id: null } : f
      );
      setNewFolders(updated);
    },
    [newFolders]
  );

  // Transform flat newFolders array to hierarchical creates array for API
  const transformNewFoldersToHierarchy = useCallback(
    (folders: any[]): any[] => {
      const creates: any[] = [];

      // Helper: Recursively build sub_folder array for NEW folders only
      const buildSubFolders = (parentId: number): any[] | null => {
        const children = folders.filter((f) => f.parent_id === parentId);
        if (children.length === 0) return null;

        return children.map((child) => ({
          name: child.name,
          description: child.description || '',
          sub_folder: buildSubFolders(child.id),
        }));
      };

      // Process all new folders
      folders.forEach((folder) => {
        // Skip folders that are nested under OTHER new folders
        const isNestedUnderNewFolder = folder.parent_id < 0;
        if (isNestedUnderNewFolder) return;

        creates.push({
          name: folder.name,
          description: folder.description || '',
          parent_id: folder.parent_id,
          sub_folder: buildSubFolders(folder.id),
        });
      });

      return creates;
    },
    []
  );

  // Handler for syncing folders to API
  const handleSync = useCallback(async () => {
    if (!projectId) {
      console.error('Project ID is not available');
      return;
    }

    if (newFolders.length === 0) {
      message.info(intl.formatMessage({ id: 'no-folders-to-reorganize' }));
      return;
    }

    try {
      const creates = transformNewFoldersToHierarchy(newFolders);

      await syncArchiveFolderStructure({
        projectId,
        updates: [],
        creates,
      });

      message.success(intl.formatMessage({ id: 'sync-success' }));

      // Clear new folders and reset state
      setNewFolders([]);
      setNextId(-1);
      setExpandedFolders(new Set());

      // Refetch existing folders
      projectBuilderQuery.refetch();
    } catch (error) {
      message.error(intl.formatMessage({ id: 'sync-error' }));
      console.error('Sync error:', error);
    }
  }, [
    newFolders,
    projectId,
    transformNewFoldersToHierarchy,
    intl,
    projectBuilderQuery,
  ]);

  // Event listener for "SYNC" button in header
  useEffect(() => {
    window.addEventListener('syncProjectBuilderFolders', handleSync);
    return () => {
      window.removeEventListener('syncProjectBuilderFolders', handleSync);
    };
  }, [handleSync]);

  // Event listener for importing template folders
  useEffect(() => {
    const handleImportTemplateFolders = (event: CustomEvent) => {
      const { folders, targetFolderId } = event.detail;

      if (!folders || folders.length === 0) return;

      // Create a mapping from old IDs to new negative IDs
      const idMapping = new Map<number, number>();
      let currentNextId = nextId;

      // First pass: assign new negative IDs to all folders
      folders.forEach((folder: any) => {
        idMapping.set(folder.id, currentNextId);
        currentNextId--;
      });

      // Second pass: transform folders with new IDs and updated parent_ids
      const transformedFolders = folders.map((folder: any) => {
        let newParentId = folder.parent_id
          ? idMapping.get(folder.parent_id) || null
          : null;

        // If targetFolderId is specified and folder is root, set it as parent
        if (
          targetFolderId !== null &&
          targetFolderId !== undefined &&
          !folder.parent_id
        ) {
          newParentId = targetFolderId;
        }

        return {
          id: idMapping.get(folder.id)!,
          name: folder.name,
          description: folder.description || '',
          parent_id: newParentId,
          isNew: true,
          is_locked: false,
          is_favorite: false,
        };
      });

      // Add to newFolders state
      setNewFolders([...newFolders, ...transformedFolders]);
      setNextId(currentNextId);

      // Auto-expand target folder if specified
      if (targetFolderId !== null && targetFolderId !== undefined) {
        setExpandedFolders(
          new Set([...Array.from(expandedFolders), targetFolderId])
        );
      }

      // Show success message
      message.success(
        intl.formatMessage({ id: 'template-folders-imported-success' })
      );
    };

    window.addEventListener(
      'importTemplateFolders',
      handleImportTemplateFolders as EventListener
    );
    return () => {
      window.removeEventListener(
        'importTemplateFolders',
        handleImportTemplateFolders as EventListener
      );
    };
  }, [newFolders, nextId, intl, expandedFolders]);

  // Root drop zone drag and drop logic
  useEffect(() => {
    const el = rootDropZoneRef.current;
    if (!el) return;

    return dropTargetForElements({
      element: el,
      getData: () => ({ type: 'root-drop-zone' }),
      canDrop: ({ source }) => {
        const folderId = source.data.folderId;
        if (typeof folderId !== 'number') {
          return false;
        }

        // Only accept new folders (negative IDs that exist in newFolders)
        const folder = newFolders.find((f) => f.id === folderId);
        return folder !== undefined; // True if it's a new folder
      },
      onDragEnter: () => {
        setIsRootDropTarget(true);
      },
      onDragLeave: () => {
        setIsRootDropTarget(false);
      },
      onDrop: ({ source }) => {
        setIsRootDropTarget(false);
        const folderId = source.data.folderId as number;
        handleMakeRoot(folderId);
      },
    });
  }, [handleMakeRoot, newFolders, isDraggingAnyFolder]);

  // Build hierarchical folder tree with sorting
  const buildFolderTree = useMemo(() => {
    const existingFolders = projectBuilderQuery.data || [];
    const allFolders = [
      ...newFolders,
      ...existingFolders.map((f) => ({ ...f, isNew: false })),
    ];

    const buildTree = (parentId: number | null): any[] => {
      const children = allFolders.filter((f) => f.parent_id === parentId);

      // Determine if parent is existing
      const parentFolder = allFolders.find((f) => f.id === parentId);
      const parentIsExisting = parentFolder && !parentFolder.isNew;

      let sorted;
      if (parentId === null) {
        // Root level: new folders (alphabetically) then existing folders (alphabetically)
        const newChildren = children
          .filter((f) => f.isNew)
          .sort((a, b) => a.name.localeCompare(b.name));
        const existingChildren = children
          .filter((f) => !f.isNew)
          .sort((a, b) => a.name.localeCompare(b.name));
        sorted = [...newChildren, ...existingChildren];
      } else {
        // Under any parent (new or existing): sort ALL children alphabetically
        sorted = children.sort((a, b) => a.name.localeCompare(b.name));
      }

      return sorted.map((folder) => ({
        ...folder,
        children: buildTree(folder.id),
      }));
    };

    return buildTree(null);
  }, [newFolders, projectBuilderQuery.data]);

  // Search filtering with tree-aware logic and auto-expand
  const filterAndExpandTree = useMemo(() => {
    if (!searchContext.query) {
      return { tree: buildFolderTree, expandIds: new Set<number>() };
    }

    const expandIds = new Set<number>();

    // Helper to check if folder or any descendant matches
    const folderMatchesOrHasMatchingChild = (folder: any): boolean => {
      const matches = filterByQuery(folder.name, searchContext.query);

      if (matches) return true;

      // Check children recursively
      if (folder.children && folder.children.length > 0) {
        return folder.children.some((child: any) =>
          folderMatchesOrHasMatchingChild(child)
        );
      }

      return false;
    };

    // Helper to filter tree while maintaining structure
    const filterTree = (folders: any[]): any[] => {
      return folders
        .map((folder) => {
          const hasMatch = folderMatchesOrHasMatchingChild(folder);

          if (!hasMatch) return null;

          // If has children, filter them too
          const filteredChildren = folder.children
            ? filterTree(folder.children)
            : [];

          // If this folder has matching children, expand it
          if (filteredChildren.length > 0) {
            expandIds.add(folder.id);
          }

          return {
            ...folder,
            children: filteredChildren,
          };
        })
        .filter(Boolean);
    };

    return {
      tree: filterTree(buildFolderTree),
      expandIds,
    };
  }, [buildFolderTree, searchContext.query]);

  // Merge search expand IDs with user's manual expand state
  const effectiveExpandedFolders = useMemo(() => {
    const merged = new Set(expandedFolders);
    Array.from(filterAndExpandTree.expandIds).forEach((id) => merged.add(id));
    return merged;
  }, [expandedFolders, filterAndExpandTree.expandIds]);

  // Helper to build folder path from root to parent
  const buildFolderPath = (folderId: number | null): string[] => {
    if (folderId === null) return [];

    const allFoldersArray = [
      ...newFolders,
      ...(projectBuilderQuery.data || []),
    ];
    const path: string[] = [];
    let currentId: number | null = folderId;

    while (currentId !== null) {
      const folder = allFoldersArray.find((f) => f.id === currentId);
      if (!folder) break;
      path.unshift(folder.name);
      currentId = folder.parent_id;
    }

    return path;
  };

  return (
    <div
      ref={containerRef}
      css={styles.container}
      style={{ height: dynamicHeight }}
      onContextMenu={(e) => e.preventDefault()}>
      <CreateFolderModal
        visible={createModalVisible}
        onCancel={() => {
          setCreateModalVisible(false);
          setCreateModalParentId(null);
        }}
        onCreate={handleCreateFolder}
        parentFolderName={
          createModalParentId
            ? [...newFolders, ...(projectBuilderQuery.data || [])].find(
                (f) => f.id === createModalParentId
              )?.name
            : undefined
        }
        parentFolderPath={buildFolderPath(createModalParentId)}
        existingFolderNames={getSiblingFolderNames(createModalParentId)}
      />
      <ReorganizeFoldersModal
        visible={reorganizeModalVisible}
        onCancel={() => setReorganizeModalVisible(false)}
        folders={newFolders}
        onSave={handleSaveReorganized}
      />
      <EditFolderProjectBuilder
        visible={editModalVisible}
        onClose={() => {
          setEditModalVisible(false);
          setRightClickFolder(null);
        }}
        onSave={handleEditFolder}
        folder={rightClickFolder}
        existingFolderNames={
          rightClickFolder
            ? getSiblingFolderNames(
                rightClickFolder.parent_id,
                rightClickFolder.id
              )
            : []
        }
      />
      <MoveFolderProjectBuilder
        visible={moveModalVisible}
        onClose={() => {
          setMoveModalVisible(false);
          setRightClickFolder(null);
        }}
        onMove={handleMoveFolder}
        folder={rightClickFolder}
        allFolders={[
          ...newFolders,
          ...(projectBuilderQuery.data || []).map((f) => ({
            ...f,
            isNew: false,
          })),
        ]}
        newFolders={newFolders}
      />
      <ImportToProjectBuilder
        visible={importToModalVisible}
        onClose={() => {
          setImportToModalVisible(false);
          setRightClickFolder(null);
        }}
        onImport={handleImportToFolder}
        targetFolder={rightClickFolder}
        allFolders={[
          ...newFolders,
          ...(projectBuilderQuery.data || []).map((f) => ({
            ...f,
            isNew: false,
          })),
        ]}
        newFolders={newFolders}
      />
      <ImportTemplateModal
        visible={importTemplateModalVisible}
        onCancel={() => {
          setImportTemplateModalVisible(false);
          setImportTemplateTargetFolder(null);
          setRightClickFolder(null);
        }}
        mode="project-builder-folder"
        targetFolderId={importTemplateTargetFolder?.id}
        targetFolderName={importTemplateTargetFolder?.name}
      />
      <BaseRightClickOverlay
        items={contextMenuItems}
        onItemClick={handleContextMenuItemClick}
        onOverlayClose={() => setRightClickFolder(null)}
        selected={{ folder: rightClickFolder }}
        showHeader={true}
        headerIcon={
          rightClickFolder?.isNew
            ? (['far', 'folder'] as [IconPrefix, IconName])
            : (['fas', 'folder'] as [IconPrefix, IconName])
        }
        headerTitle={rightClickFolder?.name}
        headerIconColor={rightClickFolder?.isNew ? '#52c41a' : '#faad14'}>
        <ProjectBuilderTable
          folderTree={filterAndExpandTree.tree}
          duplicateFolderIds={duplicateFolderIds}
          expandedFolders={effectiveExpandedFolders}
          onToggleExpand={handleToggleExpand}
          onNest={handleNestFolder}
          onRemove={handleRemoveFolder}
          canNest={canNest}
          isLoading={projectBuilderQuery.isLoading}
          rootDropZoneRef={rootDropZoneRef}
          onContextMenu={setRightClickFolder}
          rightClickedFolderId={rightClickFolder?.id ?? null}
        />
      </BaseRightClickOverlay>
      {/* Root Drop Zone - sticky at bottom, only when dragging */}
      {isDraggingAnyFolder && filterAndExpandTree.tree.length > 0 && (
        <div
          ref={rootDropZoneRef}
          css={[
            styles.rootDropZone,
            isRootDropTarget && styles.rootDropZoneActive,
          ]}>
          <FontAwesomeIcon icon={['fas', 'level-up-alt']} />
          <span>{intl.formatMessage({ id: 'drag-to-root' })}</span>
        </div>
      )}
    </div>
  );
};

const styleFn: ThemedStyleFn = (theme) => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
    padding: '12px 0',
    minHeight: '400px',
    position: 'relative',
  },
  rootDropZone: {
    position: 'sticky',
    bottom: 0,
    left: 0,
    right: 0,
    zIndex: 1000,
    padding: '20px 16px',
    margin: '12px',
    marginTop: 'auto',
    backgroundColor: 'rgba(250, 250, 250, 0.98)',
    backdropFilter: 'blur(8px)',
    borderRadius: 8,
    border: `2px dashed ${theme.colors.border || '#d9d9d9'}`,
    boxShadow: '0 -4px 12px rgba(0, 0, 0, 0.08)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 12,
    transition: 'all 0.2s ease',
    cursor: 'move',
    pointerEvents: 'auto',
    '& svg': {
      fontSize: 20,
      color: theme.colors.textSecondary || '#8c8c8c',
      transition: 'all 0.2s ease',
      pointerEvents: 'none',
    },
    '& span': {
      color: theme.colors.textSecondary || '#8c8c8c',
      fontSize: 14,
      fontWeight: 500,
      transition: 'all 0.2s ease',
      pointerEvents: 'none',
    },
  },
  rootDropZoneActive: {
    backgroundColor: 'rgba(24, 144, 255, 0.12)',
    borderColor: theme.colors.primary || '#1890ff',
    borderStyle: 'solid',
    boxShadow: '0 -6px 16px rgba(24, 144, 255, 0.2)',
    transform: 'scale(1.02)',
    '& svg': {
      color: theme.colors.primary || '#1890ff',
      transform: 'scale(1.15)',
    },
    '& span': {
      color: theme.colors.primary || '#1890ff',
      fontWeight: 600,
    },
  },
});

export default RightSideViewProjectBuilder;
